import { createSelector } from '@reduxjs/toolkit';

import EditingDocumentFieldInformation from '@app/store/editingDocument/EditingDocumentFieldInformation';
import EditingDocumentFieldTarget from '@app/store/editingDocument/EditingDocumentFieldTarget';
import { EditingDocumentIdsInPagesState } from '@app/store/editingDocument/reducers/idsInPages';
import { EditingDocumentFieldInformationState } from '@app/store/editingDocument/reducers/information';
import { EditingDocumentPositionsState } from '@app/store/editingDocument/reducers/positions';
import { EditingDocumentSizesState } from '@app/store/editingDocument/reducers/sizes';
import FieldType from '@app/store/FieldType';
import { RootState } from '@app/store/types';

export const getEditingDocumentState = (state: RootState) => state.editingDocument;

export const getEditingDocumentId = (state: RootState) =>
  getEditingDocumentState(state).editingDocumentId;
export const getFieldIds = (state: RootState) => getEditingDocumentState(state).ids;
export const getFieldIdsInPages = (state: RootState): EditingDocumentIdsInPagesState =>
  getEditingDocumentState(state).idsInPages;
export const getFieldInformation = (state: RootState): EditingDocumentFieldInformationState =>
  getEditingDocumentState(state).information;
export const getFieldPositions = (state: RootState): EditingDocumentPositionsState =>
  getEditingDocumentState(state).positions;
export const getFieldSizes = (state: RootState): EditingDocumentSizesState =>
  getEditingDocumentState(state).sizes;
export const getFieldTarget = (state: RootState): EditingDocumentFieldTarget | null =>
  getEditingDocumentState(state).target;
export const getFileUrl = (state: RootState) => getEditingDocumentState(state).url;
export const getLatestFieldData = (state: RootState) =>
  getEditingDocumentState(state).latestFieldIDByType;

export const isFieldInserting = (state: RootState) => getEditingDocumentState(state).isInserting;
export const isInsertInProgress = (state: RootState) =>
  getEditingDocumentState(state).isInsertInProgress;
export const getIsNewEditingDocument = (state: RootState) =>
  getEditingDocumentState(state).isNewEditingDocument;

export const getFieldIdsInPage = (state: RootState, props: { pageNumber: number }) => {
  const idsInPages = getFieldIdsInPages(state);
  return idsInPages[props.pageNumber] ?? null;
};

export const makeGetFieldsInPage = createSelector(
  getFieldIdsInPage,
  getFieldInformation,
  getFieldPositions,
  getFieldSizes,
  (idsInPage, information, positions, sizes) => {
    if (!idsInPage) {
      return null;
    }

    return idsInPage.map((id) => {
      const informationValue = information[id];
      const positionValue = positions[id];
      const sizeValue = sizes[id];

      return {
        ...informationValue,
        ...positionValue,
        ...sizeValue,
        id,
      };
    });
  }
);

export const isSubmittable = createSelector(getFieldInformation, (information) => {
  return (
    Object.values(information).filter((fieldInfo: EditingDocumentFieldInformation) => {
      if (fieldInfo.fieldType === FieldType.CHECKBOX_FIELD && fieldInfo.checked) {
        return true;
      }
      if (fieldInfo.fieldType === FieldType.SIGNATURE_FIELD && !!fieldInfo.s3Key) {
        return true;
      }
      if (fieldInfo.fieldType === FieldType.TEXT_FIELD && !!fieldInfo.value) {
        return true;
      }
      return false;
    }).length > 0
  );
});

export const getLatestField = createSelector(
  getLatestFieldData,
  getFieldTarget,
  getFieldSizes,
  getFieldInformation,
  (latestFieldIDByType, target, sizes, information) => {
    if (!target) {
      return null;
    }

    const { fieldType } = target;

    if (fieldType === FieldType.SIGNATURE_FIELD) return null;

    const latestFieldID = latestFieldIDByType[fieldType];

    if (!latestFieldID) {
      return null;
    }

    const informationValue = information[latestFieldID];
    const sizeValue = sizes[latestFieldID];
    return {
      ...informationValue,
      ...sizeValue,
    };
  }
);

export const getLatestTextField = createSelector(
  getLatestFieldData,
  getFieldSizes,
  getFieldInformation,
  (latestFieldIDByType, sizes, information) => {
    const latestTextFieldID = latestFieldIDByType[FieldType.TEXT_FIELD];

    if (!latestTextFieldID) {
      return null;
    }

    const informationValue = information[latestTextFieldID];
    const sizeValue = sizes[latestTextFieldID];

    return { ...informationValue, ...sizeValue };
  }
);
